import type { ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';

/**
 * The body's grid (Figma "Page Builder / Section"): two 900px cells on 48px
 * page margins with 24px between them — 48 | 900 | 24 | 900 | 48 at the 1920
 * artboard. Every module is drawn into one cell or both, so the page reads as
 * two columns from the opening picture to the credits, and a picture in the
 * left cell always meets the copy in the right on the same line.
 */
export const CELL_ROW =
    // Capped at the 1920 artboard and centred: the margins-plus-cells sum is
    // the frame's own width, and past it the row must not keep growing — an
    // uncapped row packed both cells against the left of a 4K screen while
    // every section around them stayed on the centred frame.
    'mx-auto flex w-full max-w-[120rem] flex-col gap-10 px-4.5 md:flex-row md:items-start md:gap-6 md:px-[3rem]';

/** One cell of the grid. */
export const CELL = 'w-full min-w-0 md:w-[56.25rem] md:shrink-0';

/**
 * The row taken to the landscape picture's width instead: 1520 on the page's
 * 200px margins, so a pair of pictures spans exactly what one wide picture
 * does. The 24px between the cells stays, which leaves each cell 748.
 */
const LANDSCAPE_ROW = 'md:px-[12.5rem]';
const LANDSCAPE_CELL = 'md:w-[46.75rem]';

/**
 * The landscape row's own margins, as one class string.
 *
 * Exported for the page builder, which stands its modules in the same rows
 * without rendering the reader's components into them.
 */
export const LANDSCAPE_CELL_ROW = `${CELL_ROW} ${LANDSCAPE_ROW}`;

interface RevampCellRowProps {
    /** What the left cell holds; nothing leaves it empty but still drawn. */
    left?: ReactNode;
    /** What the right cell holds. */
    right?: ReactNode;
    /** The body's 900px cells, or the pair fitted to the landscape picture's 1520. */
    width?: 'body' | 'landscape';
    className?: string;
}

/**
 * One row of the body's grid, its two cells laid out as the design lays them:
 * a picture or a quote on the left, the copy on the right. A row with one
 * side empty keeps that side's width, so a lone paragraph sits where a paired
 * one would rather than drifting to the page's centre.
 *
 * Below md the cells stack, left first, on the page's own gutter.
 */
export const RevampCellRow = ({
    left,
    right,
    width = 'body',
    className,
}: RevampCellRowProps) => {
    const landscape = width === 'landscape';
    const cell = cn(CELL, landscape && LANDSCAPE_CELL);
    return (
        <div className={cn(CELL_ROW, landscape && LANDSCAPE_ROW, className)}>
            <div className={cn(cell, !left && 'max-md:hidden')}>{left}</div>
            <div className={cn(cell, !right && 'max-md:hidden')}>{right}</div>
        </div>
    );
};
